import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { useDroppable } from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'

interface Folder {
  _id: string
  name: string
  color: string
  icon: string
  description?: string
  isDefault: boolean
  linkCount?: number
  parentFolder?: string
  children?: Folder[]
}

interface FolderTreeRendererProps {
  folders: Folder[]
  selectedFolder: string | null
  expandedFolders: Set<string>
  onFolderSelect: (folderId: string) => void
  onToggleExpanded: (folderId: string) => void
  onEditFolder: (folder: Folder) => void
  onDeleteFolder: (folder: Folder) => void
  onCreateSubFolder?: (parentFolderId: string) => void
  depth?: number
}

const FolderTreeRenderer: React.FC<FolderTreeRendererProps> = ({
  folders,
  selectedFolder,
  expandedFolders,
  onFolderSelect,
  onToggleExpanded,
  onEditFolder,
  onDeleteFolder,
  onCreateSubFolder,
  depth = 0,
}) => {
  const { t } = useTranslation()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null)
    }

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openMenuId])

  return (
    <>
      {folders.map(folder => (
        <div key={folder._id}>
          <div className="group relative">
            <DroppableFolderItem
              id={`folder-${folder._id}`}
              selected={selectedFolder === folder._id}
              onClick={() => onFolderSelect(folder._id)}
            >
              <div className="flex w-full min-w-0 items-center justify-between">
                {/* 폴더 아이콘과 이름 */}
                <div className="mr-2 flex min-w-0 flex-1 items-center">
                  {/* 들여쓰기 */}
                  <div
                    style={{ marginLeft: `${depth * 16}px` }}
                    className="flex min-w-0 flex-1 items-center"
                  >
                    {/* 확장/축소 버튼 */}
                    {folder.children && folder.children.length > 0 ? (
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          onToggleExpanded(folder._id)
                        }}
                        className="mr-1 flex-shrink-0 rounded p-0.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        {expandedFolders.has(folder._id) ? (
                          <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                    ) : (
                      <div className="mr-1 w-4 flex-shrink-0" />
                    )}

                    <div
                      className="mr-2 h-3 w-3 flex-shrink-0 rounded 3xl:mr-3 3xl:h-4 3xl:w-4"
                      style={{ backgroundColor: folder.color }}
                    />
                    <span className="truncate text-left text-sm font-medium 3xl:text-base">
                      {folder.name}
                    </span>
                  </div>
                </div>

                {/* 우측 영역: 갯수 + 편집 버튼 */}
                <div className="flex flex-shrink-0 items-center space-x-2">
                  {/* 링크 수 표시 */}
                  <div className="flex items-center space-x-1">
                    {/* 메인 링크 수 */}
                    <span className="min-w-[24px] rounded-full bg-gray-200 px-2 py-0.5 text-center text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-300 3xl:px-2.5 3xl:py-1 3xl:text-sm">
                      {folder.linkCount || 0}
                    </span>

                    {/* 서브폴더가 있는 경우 서브폴더 개수 표시 */}
                    {folder.children && folder.children.length > 0 && (
                      <span
                        className="min-w-[20px] rounded-full border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-center text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        title={t('folder.subfolderCount', {
                          count: String(folder.children.length),
                        })}
                      >
                        {folder.children.length}
                      </span>
                    )}
                  </div>

                  {/* 편집 버튼 */}
                  <div className="relative">
                    <button
                      className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300 3xl:p-1.5"
                      onClick={e => {
                        e.stopPropagation()
                        setOpenMenuId(
                          openMenuId === folder._id ? null : folder._id
                        )
                      }}
                      title={t('folder.options')}
                    >
                      <svg
                        className="h-4 w-4 3xl:h-5 3xl:w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    {/* 미니 팝업 메뉴 */}
                    {openMenuId === folder._id && (
                      <div className="absolute right-0 top-full z-50 mt-1 w-40 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        {onCreateSubFolder && (
                          <button
                            className="flex w-full items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            onClick={e => {
                              e.stopPropagation()
                              onCreateSubFolder(folder._id)
                              setOpenMenuId(null)
                            }}
                          >
                            <PlusIcon className="mr-2 h-4 w-4 text-green-500" />
                            {t('folder.createSubfolder')}
                          </button>
                        )}

                        <button
                          className="flex w-full items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                          onClick={e => {
                            e.stopPropagation()
                            onEditFolder(folder)
                            setOpenMenuId(null)
                          }}
                        >
                          <PencilIcon className="mr-2 h-4 w-4 text-blue-500" />
                          {t('folder.edit')}
                        </button>

                        {!folder.isDefault && (
                          <button
                            className="flex w-full items-center px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            onClick={e => {
                              e.stopPropagation()
                              onDeleteFolder(folder)
                              setOpenMenuId(null)
                            }}
                          >
                            <TrashIcon className="mr-2 h-4 w-4 text-red-500" />
                            {t('folder.delete')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DroppableFolderItem>
          </div>

          {/* 서브 폴더들 렌더링 */}
          {folder.children &&
            folder.children.length > 0 &&
            expandedFolders.has(folder._id) && (
              <FolderTreeRenderer
                folders={folder.children}
                selectedFolder={selectedFolder}
                expandedFolders={expandedFolders}
                onFolderSelect={onFolderSelect}
                onToggleExpanded={onToggleExpanded}
                onEditFolder={onEditFolder}
                onDeleteFolder={onDeleteFolder}
                onCreateSubFolder={onCreateSubFolder}
                depth={depth + 1}
              />
            )}
        </div>
      ))}
    </>
  )
}

// 드롭 가능한 폴더 아이템 컴포넌트
const DroppableFolderItem: React.FC<{
  id: string
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({ id, selected, onClick, children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
  })

  return (
    <button
      ref={setNodeRef}
      onClick={onClick}
      className={`flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors 3xl:px-3 3xl:py-2 3xl:text-base 7xl:px-4 7xl:py-2.5 7xl:text-lg ${
        selected
          ? 'border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
      } ${
        isOver
          ? 'border-2 border-dashed border-blue-300 bg-blue-100 dark:border-blue-600 dark:bg-blue-900/30'
          : ''
      }`}
    >
      {children}
    </button>
  )
}

export default FolderTreeRenderer
